/**
 * What an array path's `data_type` spelling says about it: the element type,
 * and the declared element count.
 *
 * `Array` is a coarse canonical type: ingestion stamps it on every sequence
 * field, so one canonical type covers a 7-joint `float64[7]`, a point cloud's
 * `uint8[]` payload, and an array of nested messages. The element type is only
 * recoverable from the source-schema spelling in `data_type`, which is what
 * this module reads. Producers that know how many elements an array has also
 * record that count there, as a trailing `[N]`, and nowhere else.
 *
 * Which ingestion produced a topic decides what spelling arrives:
 *
 * - ROS bags, `ros1msg`/`ros2msg` MCAP, and OMG IDL (`omgidl`/`ros2idl`) MCAP
 *   all label their fields through one ROS-style namer, so they arrive with a
 *   bracketed ROS spelling — `float64[7]` from a `.msg`, `float64[]` from an
 *   IDL `sequence<double>`, `float64[2][3]` from an IDL matrix. These are the
 *   spellings `arrayDefaultsToPlot` checks against
 *   `PLOTTABLE_ELEMENT_DATA_TYPES`.
 * - ULog types numeric arrays `NumberArray` with a C spelling (`float[4]`,
 *   `uint16_t[8]`), which needs no element inspection. Only its fixed-size
 *   `bool[N]` reaches `Array`.
 * - Protobuf MCAP keeps a protobuf spelling (`double[]`, `int32[]`), which
 *   `arrayDefaultsToPlot` checks against the same set, so it is judged by
 *   whether protobuf spells the element the way ROS does.
 * - `jsonschema` MCAP channels arrive as the bare literal `array`, or as
 *   `number[7]` / `integer[7]` / `number[]` / `integer[]` when ingestion has
 *   observed the element type (and a fixed element count, where there is
 *   one) and written them into `data_type`.
 * - The SDK's Parquet message-path generation records the Arrow type string,
 *   whose list field is named `element` or `item` depending on how the table
 *   was built: a variable-length `list<element: double>` carries no count; a
 *   fixed-size `fixed_size_list<element: double>[3]` carries one in its
 *   bracket.
 *
 * Current ingestion types a flat, single-bracket, non-byte numeric array from
 * protobuf, ROS or IDL as `NumberArray`, and a `jsonschema` `number[N]`,
 * `integer[N]`, `number[]` or `integer[]` likewise. Topics ingested before
 * that still arrive as `Array`, with the spellings above.
 */
import { CanonicalDataType, type MessagePathRecord } from "./MessagePathRecord";

/**
 * Element types worth charting by default, one line per index.
 *
 * Byte-width types (`uint8`, `int8`, `byte`, `char`) are deliberately absent:
 * they overwhelmingly spell binary payloads rather than measurements —
 * `sensor_msgs/PointCloud2.data`, serialized buffers — and the plot panel
 * emits one chart dataset per array element, so charting one would try to draw
 * a line per byte.
 *
 * This is a heuristic for picking a default, NOT a bound on how many lines a
 * plot can be asked to draw. It filters element type, and nothing about an
 * element type constrains an array's length: an unbounded `float32[]` is
 * admitted here, and a 1000-beam laser scan is spelled exactly that way.
 * The plot panel bounds the dataset count separately, drawing at most
 * `maxDatasetsPerSeries` elements of one series. A byte array that really is
 * a measurement is still plottable on purpose — from the topic tree's context
 * menu, or by asking for a plot panel by name.
 *
 * These are ROS spellings: ingestion labels ROS `.msg` and OMG IDL schemas
 * alike through one ROS-style namer, so an IDL `sequence<double>` arrives as
 * `float64[]` rather than carrying its own `double` spelling. Protobuf keeps
 * its own spelling and is matched against these same names, so `int32[]` is
 * admitted while `double[]` is not.
 *
 * A source that labels its own fields would need its names added as a set, and
 * the names to add are the ones it *writes* — not the ones its schemas are
 * written in, which is a distinction IDL makes vivid.
 */
const PLOTTABLE_ELEMENT_DATA_TYPES = new Set([
  "bool",
  "float32",
  "float64",
  "int16",
  "int32",
  "int64",
  "uint16",
  "uint32",
  "uint64",
]);

/**
 * The element type spelled inside an array `data_type`, or undefined when the
 * type spells no array or names no element.
 *
 * Handles the three array spellings a source schema can carry — unbounded
 * (`float64[]`), fixed-size (`float64[7]`), and ROS 2 bounded
 * (`float64[<=10]`) — by reading everything ahead of the first bracket. A
 * namespaced message type comes back whole (`geometry_msgs/Point[]` ->
 * `geometry_msgs/Point`) so callers can tell an array of messages from an
 * array of primitives.
 */
export function arrayElementDataType(dataType: string): string | undefined {
  const bracketIndex = dataType.indexOf("[");
  if (bracketIndex === -1) {
    return undefined;
  }

  const elementType = dataType.slice(0, bracketIndex).trim();
  return elementType.length > 0 ? elementType : undefined;
}

/**
 * The `list<` opener inside `list<`, `large_list<`, and `fixed_size_list<`;
 * each match is one Arrow array dimension.
 */
const ARROW_LIST_OPENER = /list</g;

/**
 * Whether an array `data_type` spells more than one dimension. A ROS-style
 * spelling does it with a second bracket group (`float64[2][3]`); an Arrow
 * spelling does it by nesting one list type inside another
 * (`list<element: list<element: double>>`,
 * `list<element: fixed_size_list<element: double>[2]>`), where the bracket
 * belongs to the fixed-size list and counts no extra dimension. Protobuf has
 * no nesting of its own, and the bare `jsonschema` literal `array` carries no
 * dimension to count.
 */
export function hasMultipleArrayDimensions(dataType: string): boolean {
  const arrowLists = dataType.match(ARROW_LIST_OPENER)?.length ?? 0;
  if (arrowLists > 0) {
    return arrowLists > 1;
  }
  return dataType.indexOf("[") !== dataType.lastIndexOf("[");
}

const DECLARED_ELEMENT_COUNT = /^[^[]+\[(\d+)\]$/;

/**
 * The element count an array `data_type` declares, read from a single
 * trailing `[N]` after an element type: `float64[7]`, `float[4]`, and
 * `fixed_size_list<element: double>[3]` read 7, 4, and 3. Undefined for a
 * variable-length array (`float64[]`, `list<element: double>`), a ROS 2
 * bounded one (`float64[<=10]`), a multi-dimensional one (`float64[2][3]`,
 * whose elements are rows), and a scalar, so undefined does not mean "not an
 * array".
 */
export function declaredElementCount(dataType: string): number | undefined {
  const match = DECLARED_ELEMENT_COUNT.exec(dataType);
  return match === null ? undefined : Number(match[1]);
}

/**
 * Whether a `CanonicalDataType.Array` path should open on a plot rather than a
 * raw view. Only that one coarse type needs asking; every other canonical type
 * decides for itself, `NumberArray` included.
 *
 * A multi-dimensional array never does, whatever its elements: the plot
 * unwraps one `[:]` level, so for `m[:]` on a `float64[2][3]` each value is a
 * whole row, a list the chart cannot draw (it shows as a gap), and the
 * message-path grammar has no `m[i][j]` form to pick out a number.
 */
export function arrayDefaultsToPlot(messagePath: MessagePathRecord): boolean {
  if (messagePath.canonical_data_type !== CanonicalDataType.Array) {
    return false;
  }
  if (hasMultipleArrayDimensions(messagePath.data_type)) {
    return false;
  }

  const elementType = arrayElementDataType(messagePath.data_type);
  return (
    elementType !== undefined &&
    PLOTTABLE_ELEMENT_DATA_TYPES.has(elementType.toLowerCase())
  );
}
